import { AxiosRequestConfig, CancelToken } from "axios";
import Api from "src/spintr/SpintrApi";

const baseUrl = "/api/v1/Saml2ServiceProviders";

/**
 * The model for creating a new SAML 2.0 service provider.
 */
export type Saml2ServiceProviderModel = Omit<
    Spintr.Saml2ServiceProvider,
    "id" | "createdAt" | "certificates" | "instanceId"
>;

/**
 * Queries SAML 2.0 service providers.
 * 
 * @param skip The number of records to skip.
 * @param take The number of records to take.
 * @param cancelToken An optional cancel token.
 * @returns A promise that resolves to a query envelope containing the SAML 2.0 service providers.
 */
async function querySaml2ServiceProvidersAsync(
    skip: number = 0,
    take: number = 10,
    cancelToken?: CancelToken
): Promise<Spintr.QueryEnvelope<Spintr.Saml2ServiceProvider>> {
    const config: AxiosRequestConfig = { params: { skip, take }, cancelToken };

    const response = await Api.get<Spintr.QueryEnvelope<Spintr.Saml2ServiceProvider>>(
        baseUrl,
        config,
    );

    return response.data;
}

/**
 * Finds a SAML 2.0 service provider by its ID.
 * 
 * @param id The ID of the SAML 2.0 service provider.
 * @param cancelToken An optional cancel token.
 * @returns A promise that resolves to the SAML 2.0 service provider.
 */
async function findSaml2ServiceProviderAsync(
    id: Spintr.Saml2ServiceProvider["id"],
    cancelToken?: CancelToken
): Promise<Spintr.Saml2ServiceProvider> {
    const response = await Api.get<Spintr.Saml2ServiceProvider>(`${baseUrl}/${id}`, { cancelToken });

    return response.data;
}

async function createSaml2ServiceProviderAsync(
    model: Saml2ServiceProviderModel,
    cancelToken?: CancelToken
): Promise<Saml2ServiceProviderModel> {
    const response = await  Api.post<Saml2ServiceProviderModel>(baseUrl, model, { cancelToken });

    return response.data;
}

async function updateSaml2ServiceProviderAsync(
    id: Spintr.Saml2ServiceProvider["id"],
    model: Saml2ServiceProviderModel,
    cancelToken?: CancelToken
): Promise<Saml2ServiceProviderModel> {
    const response = await  Api.put<Saml2ServiceProviderModel>(`${baseUrl}/${id}`, model, { cancelToken });

    return response.data;
}

export {
    querySaml2ServiceProvidersAsync,
    findSaml2ServiceProviderAsync,
    createSaml2ServiceProviderAsync,
    updateSaml2ServiceProviderAsync,
};
